import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/api";
import { I18nInputs } from "@/components/admin/ArtistsTab";
import { ImageUpload } from "@/components/admin/ImageUpload";

const inputCls = "bg-transparent border border-white/15 px-3 py-2 text-sm outline-none focus:border-white transition-colors w-full";

export const SettingsTab = ({ settings, refresh }) => {
  const [form, setForm] = useState(() => JSON.parse(JSON.stringify(settings || {})));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setChapter = (i, k, v) =>
    setForm((f) => {
      const chapters = f.chapters.map((c, j) => (j === i ? { ...c, [k]: v } : c));
      return { ...f, chapters };
    });

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/settings", form);
      toast.success("Configurações salvas");
      refresh();
    } catch { toast.error("Erro ao salvar"); }
  };

  return (
    <form onSubmit={save} className="grid gap-6 max-w-3xl" data-testid="admin-settings-tab">
      <h2 className="font-display uppercase text-3xl tracking-wider">Configurações do site</h2>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="grid gap-1">
          <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-500">Nome do studio</span>
          <input value={form.studio_name || ""} onChange={(e) => set("studio_name", e.target.value)} data-testid="settings-studio-name" className={inputCls} />
        </label>
        <label className="grid gap-1">
          <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-500">Cidade (linha do hero)</span>
          <input value={form.city_line || ""} onChange={(e) => set("city_line", e.target.value)} data-testid="settings-city-line" className={inputCls} />
        </label>
      </div>

      <div className="border border-white/10 p-4 grid gap-4" data-testid="settings-hero-media">
        <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-500">Fundo do hero (topo do site)</span>
        <label className="grid gap-1">
          <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-500">Vídeo de fundo (tem prioridade sobre a imagem)</span>
          <ImageUpload value={form.hero_video || ""} onChange={(p) => set("hero_video", p)} testid="settings-hero-video" accept="video/*" kind="vídeo" />
        </label>
        <label className="grid gap-1">
          <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-500">Imagem de fundo</span>
          <ImageUpload value={form.hero_image || ""} onChange={(p) => set("hero_image", p)} testid="settings-hero-image" kind="imagem" />
        </label>
      </div>

      <I18nInputs label="Tagline (hero)" prefix="settings-tagline" value={form.tagline || { pt: "", en: "", de: "" }} onChange={(v) => set("tagline", v)} />
      <I18nInputs label="Texto do letreiro (marquee)" prefix="settings-marquee" value={form.marquee || { pt: "", en: "", de: "" }} onChange={(v) => set("marquee", v)} />
      <I18nInputs label="Horários" prefix="settings-hours" value={form.hours || { pt: "", en: "", de: "" }} onChange={(v) => set("hours", v)} />
      <I18nInputs label="Nota de agendamento" prefix="settings-booking-note" textarea value={form.booking_note || { pt: "", en: "", de: "" }} onChange={(v) => set("booking_note", v)} />

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="grid gap-1">
          <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-500">Endereço</span>
          <input value={form.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="settings-address" className={inputCls} />
        </label>
        <label className="grid gap-1">
          <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-500">Telefone / WhatsApp</span>
          <input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="settings-phone" className={inputCls} />
        </label>
        <label className="grid gap-1">
          <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-500">Email</span>
          <input value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="settings-email" className={inputCls} />
        </label>
        <label className="grid gap-1">
          <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-500">Instagram (URL)</span>
          <input value={form.instagram || ""} onChange={(e) => set("instagram", e.target.value)} data-testid="settings-instagram" className={inputCls} />
        </label>
      </div>

      <label className="grid gap-1">
        <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-500">Código do widget de agendamento (HTML/iframe)</span>
        <textarea rows={4} value={form.widget_embed_code || ""} onChange={(e) => set("widget_embed_code", e.target.value)}
          data-testid="settings-widget-code" placeholder='Cole aqui o código do seu widget, ex: <iframe src="..."></iframe>'
          className={`${inputCls} font-mono2 text-xs resize-y`} />
      </label>

      <div className="border-t border-white/10 pt-6 grid gap-6">
        <h3 className="font-display uppercase text-2xl tracking-wider">Manifesto (capítulos)</h3>
        {(form.chapters || []).map((c, i) => (
          <div key={i} className="border border-white/10 p-4 grid gap-4" data-testid={`settings-chapter-${i}`}>
            <span className="font-display text-xl text-outline">{String(i + 1).padStart(2, "0")}</span>
            <I18nInputs label="Título" prefix={`chapter-${i}-title`} value={c.title} onChange={(v) => setChapter(i, "title", v)} />
            <I18nInputs label="Texto" prefix={`chapter-${i}-text`} textarea value={c.text} onChange={(v) => setChapter(i, "text", v)} />
          </div>
        ))}
      </div>

      <button type="submit" data-testid="settings-save-btn"
        className="bg-white text-black font-mono2 text-[10px] tracking-[0.2em] uppercase px-8 py-4 hover:bg-[#E63946] hover:text-white transition-colors w-fit">
        Salvar configurações
      </button>
    </form>
  );
};
